import React from "react";
import "./ScientistProfile.css";

import burnPortrait from "../../../assets/substratum-protocol/scientist-burnstein-grok.webp";

const defaultScientist = {
  name: "Dr. Burnstein Grok",
  callSign: "Burn",
  pronouns: "He/Him",
  age: "Mid-50s",
  nationality: "American",
  specialty: "Hydrology / Oceanography",
  base: "Nome, Alaska",
  affiliations: "University of Alaska Fairbanks / Petroleum Industry Research",
  expeditionRole:
    "Hydrology, wave patterns, oceanic phenomena, and shifting currents",

  image: burnPortrait,

  physicalDescription:
    "Burn is a weathered, broad-shouldered man who often looks more like a stevedore than a scientist. He is most comfortable in practical work clothes, typically wearing Dickies, Carhartt work pants, and black boots. Despite his wealth and professional reputation, he has little interest in dressing like an academic.",

  physicalQuote: "Call me Burn.",

  personality:
    "Unpretentious, practical, observant, and quietly confident. Burn has little patience for academic posturing and prefers people who demonstrate what they know rather than explain how impressive their credentials are. He has a deep respect for nature and the forces that shape it, particularly water, currents, ice, and weather.",

  strengths: [
    "Exceptional understanding of water and fluid movement",
    "Extensive field experience",
    "Comfortable in hazardous environments",
    "Strong observational skills",
    "Practical and mechanically minded",
    "Highly respected within his field",
  ],

  weaknesses: [
    "Unpretentious to the point of seeming dismissive",
    "Distrustful of unnecessary bureaucracy",
    "More comfortable in the field than formal settings",
    "Can become stubborn when observations contradict accepted theory",
    "Increasingly solitary after recent family losses",
  ],

  recruitment: {
    title: "How Did He End Up on the Team?",
    paragraphs: [
      "Burn's expertise in hydrology and oceanic phenomena made him a natural candidate for an expedition into an environment where conventional assumptions about Earth's physical systems may no longer apply.",
      "His work on offshore rigs studying wave patterns and oceanic phenomena has given him extensive experience in dangerous environments where a mistake can quickly become fatal.",
      "The Substratum expedition needs someone who understands what happens when an apparently stable natural system suddenly isn't stable anymore.",
    ],
  },

  research: {
    title: "What Kind of Research Has He Done in the Past?",
    paragraphs: [
      "Burn's breakthrough research focused on new, naturally occurring currents forming in the northern oceans.",
      "He proved that these currents were shifting and could create new pathways for travel across the Arctic. His work demonstrated that the northern oceans were not as static or predictable as previously believed.",
      "The implications of his research extended well beyond science. His studies helped identify where future offshore development and investment could become viable, making his research extremely valuable to petroleum companies.",
      "The success of this work made Burn both rich and highly respected, though very little about his appearance or lifestyle suggests either.",
    ],
  },

  dilemma: {
    title: "What Was a Moral Work Dilemma and How Did He Handle It?",
    paragraphs: [
      "Burn's research sits at the intersection of scientific discovery and commercial interest. His work on northern currents has significant implications for future offshore development and petroleum investment.",
      "Petroleum companies provide support for his research, creating an unavoidable tension between scientific investigation and commercial interests.",
      "Burn's approach is pragmatic. He believes that if the research is sound, the information should be accurate and the implications should be understood. What others choose to do with that knowledge is ultimately their responsibility.",
    ],
  },

  background: {
    title: "Personal Background",
    paragraphs: [
      "Burn currently works out of Nome, Alaska through the University of Alaska Fairbanks, with additional support from petroleum companies.",
      "His career has taken him onto offshore rigs and into remote marine environments where he studies wave patterns, currents, and unusual oceanic phenomena.",
      "Burn has no immediate family. His brother and father both passed recently. He rarely discusses the subject unless directly asked.",
      "The losses have left him increasingly solitary. He has always been comfortable spending long periods away from civilization.",
    ],
  },

  interests: {
    title: "Personal Interests & Hobbies",
    paragraphs: [
      "Burn writes nature poetry. His poems focus on water, ice, tides, currents, weather, and the changing landscape.",
      "Beneath the natural imagery are recurring ideas about physics and time. Burn uses poetry to explore concepts that are difficult to express through conventional scientific language.",
      "He doesn't consider himself a poet. If pressed, he describes what he writes as observations that happen to be written down differently.",
    ],
  },

  exosuit: {
    title: "Any Exosuit Experience?",
    paragraphs: [
      "Burn has hazardous outfit experience, including scuba diving and wet suits.",
      "Years of working around offshore equipment and in dangerous aquatic environments have made him comfortable trusting specialized equipment with his life.",
      "He isn't an exosuit engineer, but he understands how to operate equipment in environments where equipment failure can quickly become life-threatening.",
    ],
  },

  skills: [
    {
      name: "THINK",
      die: "d8",
      rationale:
        "Exceptional observation, analysis, and understanding of complex natural systems.",
    },
    {
      name: "DASH",
      die: "d4",
      rationale:
        "Not as good as getting out of situations as good as getting into situations.",
    },
    {
      name: "MAKE",
      die: "d10",
      rationale:
        "Practical, hands-on problem solving developed through years of fieldwork.",
    },
    {
      name: "BREAK",
      die: "d4",
      rationale: "Not his primary area of expertise.",
    },
    {
      name: "EVADE",
      die: "d4",
      rationale:
        "Burn relies more on preparation and experience than avoidance.",
    },
    {
      name: "SWAY",
      die: "d6",
      rationale: "Straightforward and unpretentious.",
    },
  ],

  roleplaying: {
    voice: "Low, relaxed, and conversational",
    demeanor: "Calm, practical, and observant",
    humor: "Dry and understated",
    dangerResponse: "Well. That's new.",
    hook: "Burn understands that natural systems change. His entire career has been built around recognizing changes in currents that other scientists overlooked. Now he is entering an environment where the entire planet may be undergoing a change he cannot predict.",
    quote: "Water always finds a way. The question is where it's going.",
  },
};

function Section({ title, children, className = "" }) {
  return (
    <section className={`profile-section ${className}`}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function Paragraphs({ paragraphs = [] }) {
  return (
    <>
      {paragraphs.map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
    </>
  );
}

function ScientistProfile_Burn({ scientist = defaultScientist }) {
  return (
    <article className="scientist-profile">
      {/* =========================
          LEFT COLUMN
      ========================== */}

      <aside className="profile-sidebar">
        {/* Portrait */}

        <div className="portrait-frame">
          <img src={scientist.image} alt={`${scientist.name} portrait`} />
        </div>

        {/* Physical Description */}

        <Section title="Physical Description">
          <p>{scientist.physicalDescription}</p>

          {scientist.physicalQuote && (
            <p className="profile-quote-small">"{scientist.physicalQuote}"</p>
          )}
        </Section>

        {/* Personality */}

        <Section title="Personality Profile">
          <p>{scientist.personality}</p>
        </Section>

        {/* Strengths / Weaknesses */}

        <Section title="Strengths & Weaknesses">
          <div className="strength-group">
            <h3>Strengths</h3>

            <ul>
              {scientist.strengths.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="weakness-group">
            <h3>Weaknesses</h3>

            <ul>
              {scientist.weaknesses.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </Section>

        {/* Skills */}

        <Section title="Substratum Skills">
          <table className="skills-table">
            <thead>
              <tr>
                <th>Skill</th>
                <th>Die</th>
                <th>Rationale</th>
              </tr>
            </thead>

            <tbody>
              {scientist.skills.map((skill) => (
                <tr key={skill.name}>
                  <td className="skill-name">{skill.name}</td>

                  <td className="skill-die">{skill.die}</td>

                  <td>{skill.rationale}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="skills-note">
            Standard character creation uses one d10, one d8, one d6, and three
            d4s.
          </p>
        </Section>
      </aside>

      {/* =========================
          RIGHT COLUMN
      ========================== */}

      <main className="profile-main">
        {/* Header */}

        <header className="profile-header">
          <div className="eyebrow">PERSONNEL OVERVIEW</div>

          <h1>{scientist.name}</h1>

          <div className="personnel-info">
            <div className="info-row">
              <strong>Call Sign:</strong>
              <span>{scientist.callSign}</span>
            </div>

            <div className="info-row">
              <strong>Pronouns:</strong>
              <span>{scientist.pronouns}</span>
            </div>

            <div className="info-row">
              <strong>Age:</strong>
              <span>{scientist.age}</span>
            </div>

            <div className="info-row">
              <strong>Nationality:</strong>
              <span>{scientist.nationality}</span>
            </div>

            <div className="info-row">
              <strong>Scientific Specialty:</strong>
              <span>{scientist.specialty}</span>
            </div>

            <div className="info-row">
              <strong>Current Base:</strong>
              <span>{scientist.base}</span>
            </div>

            <div className="info-row">
              <strong>Affiliations:</strong>
              <span>{scientist.affiliations}</span>
            </div>

            <div className="info-row">
              <strong>Expedition Role:</strong>
              <span>{scientist.expeditionRole}</span>
            </div>
          </div>
        </header>

        {/* Recruitment */}

        <Section title={scientist.recruitment.title}>
          <Paragraphs paragraphs={scientist.recruitment.paragraphs} />
        </Section>

        {/* Research */}

        <Section title={scientist.research.title}>
          <Paragraphs paragraphs={scientist.research.paragraphs} />
        </Section>

        {/* Moral Dilemma */}

        <Section title={scientist.dilemma.title}>
          <Paragraphs paragraphs={scientist.dilemma.paragraphs} />
        </Section>

        {/* Personal Background */}

        <Section title={scientist.background.title}>
          <Paragraphs paragraphs={scientist.background.paragraphs} />
        </Section>

        {/* Interests */}

        <Section title={scientist.interests.title}>
          <Paragraphs paragraphs={scientist.interests.paragraphs} />
        </Section>

        {/* Exosuit Experience */}

        <Section title={scientist.exosuit.title}>
          <Paragraphs paragraphs={scientist.exosuit.paragraphs} />
        </Section>

        {/* =========================
            BOTTOM ROLEPLAYING AREA
        ========================== */}
        {/* 
        <div className="bottom-grid">
          <section className="bottom-panel">
            <h2>Field Notes</h2>

            <p>
              <strong>Primary Expertise:</strong> Hydrology and oceanic systems
            </p>

            <p>
              <strong>Field Experience:</strong> Offshore rigs, scuba, wet
              suits, and hazardous marine environments
            </p>

            <p>
              <strong>Research Focus:</strong> Wave patterns, oceanic phenomena,
              and shifting northern currents
            </p>

            <p>
              <strong>Professional Status:</strong> Wealthy and highly respected
              following the success of his Arctic current research
            </p>
          </section>

          <section className="bottom-panel roleplaying-panel">
            <h2>Roleplaying Notes</h2>

            <p>
              <strong>Voice:</strong> {scientist.roleplaying.voice}
            </p>

            <p>
              <strong>Demeanor:</strong> {scientist.roleplaying.demeanor}
            </p>

            <p>
              <strong>Humor:</strong> {scientist.roleplaying.humor}
            </p>

            <p>
              <strong>Default Response to Danger:</strong>{" "}
              {scientist.roleplaying.dangerResponse}
            </p>

            <p>
              <strong>Core Character Hook:</strong> {scientist.roleplaying.hook}
            </p>

            <blockquote>{scientist.roleplaying.quote}</blockquote>
          </section>
        </div> */}
      </main>
    </article>
  );
}

export default ScientistProfile_Burn;
